import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { Box, Group, Stack } from '@mantine/core'
import { AppButton, AppInput, AppSwitch, AppText, AppTextarea } from '../../app/common/ui'
import { toast } from 'react-toastify'
import agent from '../../app/api/agent'
import { useStore } from '../../app/stores/store'
import { canCreatePoll, MIN_AMMO_SCORE_TO_CREATE_POLL } from '../../app/common/constants/reputation'
import type { CacheListItemDto } from '../../app/models/cache'

export default observer(function CreatePollPage () {
  const { userStore } = useStore()
  const history = useHistory()
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const cacheId = queryParams.get('cacheId') ?? undefined
  const [question, setQuestion] = useState('')
  const [description, setDescription] = useState('')
  const [options, setOptions] = useState(['', ''])
  const [allowEarlyReveal, setAllowEarlyReveal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [selectedCache, setSelectedCache] = useState<CacheListItemDto | null>(null)
  const [userCaches, setUserCaches] = useState<CacheListItemDto[]>([])
  const [loadingCaches, setLoadingCaches] = useState(false)

  const loadUserCaches = useCallback(async () => {
    if (!userStore.user) return
    setLoadingCaches(true)
    try {
      const caches = await agent.Caches.listMyJoinedCaches(100)
      setUserCaches(caches)
      if (cacheId) {
        const found = caches.find(c => c.id === cacheId)
        if (found) setSelectedCache(found)
      }
    } catch (error) {
      /* ignored */
    } finally {
      setLoadingCaches(false)
    }
  }, [userStore.user, cacheId])

  useEffect(() => {
    if (userStore.isLoggedIn) {
      void loadUserCaches()
    }
  }, [userStore.isLoggedIn, loadUserCaches])

  if (!userStore.isLoggedIn) {
    return (
      <Box sx={{ padding: 24, maxWidth: 560, margin: '0 auto' }}>
        <AppText mb="md">Log in to create a poll.</AppText>
        <AppButton component={Link} to="/login" color="ember">
          Log in
        </AppButton>
      </Box>
    )
  }

  if (!canCreatePoll(userStore.user)) {
    return (
      <Box sx={{ padding: 24, maxWidth: 560, margin: '0 auto' }}>
        <AppText mb="md">
          AmmoScore {MIN_AMMO_SCORE_TO_CREATE_POLL}+ required to create polls.
        </AppText>
        <AppButton component={Link} to="/polls" variant="light">
          Back
        </AppButton>
      </Box>
    )
  }

  const updateOption = (index: number, value: string) => {
    setOptions((prev) => prev.map((o, i) => (i === index ? value : o)))
  }

  const addOption = () => {
    if (options.length >= 10) return
    setOptions((prev) => [...prev, ''])
  }

  const removeOption = (index: number) => {
    if (options.length <= 2) return
    setOptions((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    const trimmed = options.map((o) => o.trim()).filter(Boolean)
    if (!question.trim()) {
      toast.error('Question is required')
      return
    }
    if (trimmed.length < 2) {
      toast.error('At least two options are required')
      return
    }
    setSubmitting(true)
    try {
      const poll = await agent.Polls.create({
        question: question.trim(),
        description: description.trim(),
        options: trimmed,
        allowEarlyReveal,
        cacheId: selectedCache?.id
      })
      toast.success('Poll created')
      if (selectedCache) {
        history.push(`/caches/${selectedCache.id}`)
      } else {
        history.push(`/polls/${poll.id}`)
      }
    } catch (error: any) {
      const message = error?.response?.data?.detail || 'Failed to create poll'
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box sx={{ padding: '16px 24px', maxWidth: 560, margin: '0 auto' }}>
      <AppButton component={Link} to={selectedCache ? `/caches/${selectedCache.id}` : '/polls'} variant="subtle" size="xs" mb="md">
        ← {selectedCache ? selectedCache.name : 'Polls'}
      </AppButton>

      <AppText sx={{ fontFamily: 'Oswald, sans-serif', fontWeight: 600, fontSize: 20, mb: 'md' }}>
        {selectedCache ? `NEW POLL · ${selectedCache.name}` : 'NEW SITEWIDE POLL'}
      </AppText>

      <Stack spacing="md">
        {userCaches.length > 0 && (
          <Stack spacing="xs">
            <AppText size="sm" weight={500}>Scope</AppText>
            <Group spacing="xs">
              <AppButton
                variant={selectedCache == null ? 'filled' : 'outline'}
                color={selectedCache == null ? 'ember' : 'gray'}
                size="xs"
                onClick={() => setSelectedCache(null)}
              >
                Sitewide
              </AppButton>
              {userCaches.map(cache => (
                <AppButton
                  key={cache.id}
                  variant={selectedCache?.id === cache.id ? 'filled' : 'outline'}
                  color={selectedCache?.id === cache.id ? 'ember' : 'gray'}
                  size="xs"
                  onClick={() => setSelectedCache(cache)}
                  sx={{ fontSize: 12 }}
                >
                  {cache.name}
                </AppButton>
              ))}
            </Group>
          </Stack>
        )}

        <AppInput
          label="Question"
          value={question}
          onChange={(e) => setQuestion(e.currentTarget.value)}
          required
          maxLength={500}
        />
        <AppTextarea
          label="Description (optional)"
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          minRows={2}
        />
        <AppText size="sm" weight={500}>
          Options
        </AppText>
        {options.map((opt, i) => (
          <Group key={i} spacing="xs" noWrap>
            <AppInput
              sx={{ flex: 1 }}
              placeholder={`Option ${i + 1}`}
              value={opt}
              onChange={(e) => updateOption(i, e.currentTarget.value)}
              maxLength={500}
            />
            {options.length > 2 && (
              <AppButton variant="subtle" size="xs" color="gray" onClick={() => removeOption(i)}>
                Remove
              </AppButton>
            )}
          </Group>
        ))}
        {options.length < 10 && (
          <AppButton variant="light" size="xs" onClick={addOption}>
            + Add option
          </AppButton>
        )}
        <AppSwitch
          label="Show results before poll ends"
          checked={allowEarlyReveal}
          onChange={(e) => setAllowEarlyReveal(e.currentTarget.checked)}
        />
        <AppText size="xs" color="dimmed">
          Polls run for 24 hours. {selectedCache ? `This poll is visible to ${selectedCache.name} members.` : 'This poll is visible to everyone on GunSlop.'}
        </AppText>
        <AppButton color="ember" loading={submitting} onClick={() => void handleSubmit()}>
          Create poll
        </AppButton>
      </Stack>
    </Box>
  )
})
